import i18n from '@/i18n';
// Типи консолідованого ендпоінта /financial/result/overview + палітра графіків
// на дизайн-токенах (догма: генерація→зелений, увага→бурштин, червоний лише
// критичне). Формати переекспортуємо зі старого types.ts, щоб не дублювати.

export { fmtUah, fmtUah2, fmtMwh, fmtPrice, fmtSignMwh } from './types';

// Bare-числа БЕЗ одиниць — для клітинок таблиць, де одиницю вже названо в
// заголовку/назві рядка (не дублюємо «грн» у кожній клітинці).
const _nf = (min: number, max: number) => new Intl.NumberFormat((i18n.language === 'en' ? 'en-GB' : 'uk-UA'), { minimumFractionDigits: min, maximumFractionDigits: max });
export const fmtNum0 = (v: number | null | undefined, dash = '—') =>
  v == null || Number.isNaN(v) ? dash : _nf(0, 0).format(Math.round(v));
export const fmtNum2 = (v: number | null | undefined, dash = '—') =>
  v == null || Number.isNaN(v) ? dash : _nf(2, 2).format(Number(v));

export type FinGran = 'hour' | 'day';
export type FinPeriodKind = 'day' | 'month' | 'custom';

export interface OverviewRange { from: string; to: string; label: string }

export interface MonthCloseStatus {
  closeMonth: string;
  monthEnd?: string;
  isClosed: boolean;
  mode: 'manual' | 'auto' | 'open';
  manualClosed?: boolean;
  closedAt?: string | null;
  closedBy?: string | null;
  actualDays?: number;
  monthOver?: boolean;
}

export interface SummaryBlock {
  plannedIncomeUah: number | null;
  imbalanceCostUah: number | null;
  lostProfitUah: number;
  financialResultUah: number | null;
  deltaPct: {
    plannedIncome: number | null;
    imbalance: number | null;
    financialResult: number | null;
  };
}

export interface PlannedIncomeRow {
  key: number | string;           // hour(1..24) | 'YYYY-MM-DD'
  releaseMwh: number | null;
  priceUahMwh: number | null;
  priceModel: string | null;      // fixed | formula | null
  incomeUah: number | null;
  cumulativeUah: number | null;
  saleTermId: number | null;
}

export interface PlannedIncomeBlockData {
  rows: PlannedIncomeRow[];
  weightedAvgPriceUahMwh: number | null;
  totalReleaseMwh: number | null;
  totalIncomeUah: number | null;
}

export interface ImbalanceRow {
  key: number | string;
  imbalanceMwh: number | null;
  imbalanceKwh: number | null;
  currentCostUah: number | null;
  actualCostUah: number | null;
  diffUah: number | null;
}

export interface ImbalanceKpi {
  plannedIncomeUah: number | null;
  currentResultUah: number | null;
  financialResultUah: number | null;
  accumFromMonthStartUah: number | null;   // KPI #4 у режимі «День»
  economicEffectUah: number | null;        // KPI #4 у режимі «Місяць/Власний»
  economicEffectPct: number | null;
}

export interface ImbalanceBlockData {
  rows: ImbalanceRow[];
  kpi: ImbalanceKpi;
  totals: { imbalanceKwh: number | null; currentCostUah: number | null; actualCostUah: number | null };
}

export interface OverviewPayload {
  assetId: string;
  assetName: string;
  wCode: string | null;
  period: FinPeriodKind;
  gran: FinGran;
  range: OverviewRange;
  monthClose: MonthCloseStatus;
  summary: SummaryBlock;
  plannedIncome: PlannedIncomeBlockData;
  imbalance: ImbalanceBlockData;
}

// ── Палітра графіків (світла/темна) за колірною догмою ────────────────────
export function finChartColors(isDark: boolean) {
  return isDark
    ? {
        axis: '#7E868E', grid: 'rgba(255,255,255,0.08)',
        release: '#4CAF6A',      // відпуск (доставлена генерація) — зелений ok
        income: '#F2F2F2',       // лінія планового доходу — біла (нейтр. контраст)
        imbPos: '#4CAF6A',       // небаланс + (надлишок) — зелений
        imbNeg: '#E0A24A',       // небаланс − (дефіцит) — бурштин warn
        costCurrent: '#F2F2F2',  // актуальна вартість — біла лінія
        costActual: '#C9CCCE',   // фактична вартість — сіра (пунктир)
        tipBg: '#2F3A45', tipLine: 'rgba(255,255,255,0.10)', tipText: '#F2F2F2',
      }
    : {
        axis: '#9AA0A6', grid: '#E4E5E7',
        release: '#2E7D46', income: '#28323C',
        imbPos: '#2E7D46', imbNeg: '#C77A1B',
        costCurrent: '#28323C', costActual: '#5E656D',
        tipBg: '#FFFFFF', tipLine: '#E4E5E7', tipText: '#28323C',
      };
}

// Підпис осі X: година 1..24 → 'HH', або 'YYYY-MM-DD' → 'DD'.
export const finAxisLabel = (key: number | string, gran: FinGran): string =>
  gran === 'hour' ? String(Number(key)).padStart(2, '0') : String(key).slice(8, 10);

// Формат Δ% для KPI (з + / − та стрілкою).
export const fmtDeltaPct = (v: number | null | undefined): { text: string; dir: 'up' | 'down' | 'flat' } => {
  if (v == null || Number.isNaN(v)) return { text: '—', dir: 'flat' };
  const dir = v > 0 ? 'up' : v < 0 ? 'down' : 'flat';
  return { text: `${v > 0 ? '+' : ''}${v.toFixed(1)}%`, dir };
};
